import React, { useState, useEffect, useMemo } from 'react';
import {
  Grid,
  Autocomplete,
  TextField,
  Checkbox,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TableContainer,
  TableCell,
  TableRow,
  TableHead,
  Table,TableBody,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import clientApi from 'src/api/clients';
import CloseIcon from '@mui/icons-material/Close';
import Scrollbar from 'src/components/Scrollbar';
import { addInvoiceGrouping,getInvoiceGrouping } from 'src/api/credits';
import Alert from '@mui/material/Alert';
import Page from 'src/components/Page';
import { getClientProperties } from 'src/api/ShiftMaster';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const DUPLICATE_CLIENT_MESSAGE = 'Client information already exists. Please use the Edit option to update the existing client details.';

const InvoiceGrouping = () => {
  const [selectAll, setSelectAll] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [clientLocationAvailability, setClientLocationAvailability] = useState({});
  const [locationSelectAll, setLocationSelectAll] = useState(false);
  const [openLocationAuto, setOpenLocationAuto] = useState(false);
  const [openAuto, setOpenAuto] = useState(false);
  const [error, setError] = useState(false);
  const [time, setTime] = useState('00:00');
  const [frequency, setFrequency] = useState(1);
  const [lastEmail, setLastEmail] = useState(false)
  // New state for additional fields
  const [address, setAddress] = useState('');
  const [email, setEmail] = useState('');
  const [groupedInvoices, setGroupedInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    address: false,
    emails: false
  });

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editTime, setEditTime] = useState('00:00');
  const [editFrequency, setEditFrequency] = useState(1);
  const [editAddress, setEditAddress] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editErrors, setEditErrors] = useState({
    clients: false,
    locations: false,
    emails: false,
    address: false,
    time: false
  });
  const [editLoading, setEditLoading] = useState(false);

  const [addEmptyBox, setEmptyBox] = useState([
      {
        email: false
      }
  ])

  const [errors, setErrors] = useState({
    clients: false,
    locations: false,
    emails: false,
    address: false,
    time: false,
    frequency: false
  });
  
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const normalizeDisplayName = (name) =>
    String(name || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();

  const buildUniqueClientOptions = (clients) => {
    const grouped = new Map();

    (clients || []).forEach((clientItem) => {
      const key = normalizeDisplayName(clientItem?.displayname);
      if (!key) return;

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key).push(clientItem);
    });

    return Array.from(grouped.values()).map((groupItems) => {
      if (groupItems.length === 1) return groupItems[0];

      const assignTargets = new Set(
        groupItems
          .map((item) => Number(item.AssignClient || 0))
          .filter((assignId) => assignId > 0)
      );
      const mappedMain = groupItems.find((item) => assignTargets.has(Number(item.ID)));
      if (mappedMain) return mappedMain;

      const rootEntry = groupItems.find((item) => Number(item.AssignClient || 0) === 0);
      return rootEntry || groupItems[0];
    });
  };

  const uniqueClientOptions = useMemo(
    () => buildUniqueClientOptions(clientList),
    [clientList]
  );

  const uniqueAvailableClients = uniqueClientOptions;
  useEffect(() => {
    clientApi.getClients((response) => {
      if (response.status === 200) {
        const usClients = response.data.filter(client => client.ClientType === "US" && client.EnvironmentType !== 0);
        const sortedData = [...usClients].sort((a, b) =>
          a.displayname.trim().localeCompare(b.displayname.trim())
        );
        setClientList(sortedData);
      }
    });

   fetchInvoiceGrouping();
  }, []);

  const fetchInvoiceGrouping = () =>{
    getInvoiceGrouping((response)=>{
      if(response.status === 200){
        //console.log(response.data)
        setGroupedInvoices(response.data)
      }
    })
  };

  const normalizeLocations = (locationResponse) => {
    const responseArray = Array.isArray(locationResponse)
      ? locationResponse
      : (locationResponse ? [locationResponse] : []);

    return responseArray
      .map((location) => ({
        ...location,
        ID: Number(location?.ID ?? location?.id ?? 0),
        Location: (location?.Location || location?.name || '').trim()
      }))
      .filter((location) => location.ID > 0 && location.Location);
  };

  const fetchLocationsByClients = (clients) => {
    if (!clients || clients.length === 0) {
      setLocations([]);
      setSelectedLocations([]);
      setClientLocationAvailability({});
      setLocationSelectAll(false);
      return;
    }

    const clientIds = clients
      .map((clientItem) => Number(clientItem?.ID || clientItem?.id || 0))
      .filter((id) => id > 0);

    Promise.all(
      clientIds.map((clientId) => new Promise((resolve) => {
        getClientProperties({ AssignClient: String(clientId) }, (response) => {
          if (response.status === 200) {
            const normalizedLocations = normalizeLocations(response.data).map((location) => ({
              ...location,
              parentClientId: clientId
            }));
            resolve({
              clientId,
              locations: normalizedLocations
            });
            return;
          }
          resolve({
            clientId,
            locations: []
          });
        });
      }))
    ).then((result) => {
      const nextClientLocationAvailability = result.reduce((acc, item) => {
        acc[item.clientId] = item.locations.length > 0;
        return acc;
      }, {});
      const allLocations = result.flatMap((item) => item.locations);
      const uniqueLocations = Array.from(
        new Map(allLocations.map((location) => [location.ID, location])).values()
      );
      setLocations(uniqueLocations);
      setSelectedLocations([]);
      setClientLocationAvailability(nextClientLocationAvailability);
      setLocationSelectAll(false);
    });
  };

  const handleClientChange = (event, value) => {
    let nextSelectedClients = [];

    if (value.some(client => client.selectAll)) {
      if (selectAll) {
        nextSelectedClients = [];
        setSelectAll(false);
      } else {
        nextSelectedClients = uniqueAvailableClients;
        setSelectAll(true);
      }
    } else {
      nextSelectedClients = value;
      setSelectAll(uniqueAvailableClients.length > 0 && value.length === uniqueAvailableClients.length);
    }

    setSelectedClients(nextSelectedClients);
    if (errors.clients && nextSelectedClients.length > 0) {
      setErrors(prev => ({ ...prev, clients: false }));
    }
    fetchLocationsByClients(nextSelectedClients);
  };

  const handleLocationFilterOptions = (options, params) => {
    const filtered = options.filter((option) =>
      (option?.Location || '').toLowerCase().includes(params.inputValue.toLowerCase())
    );
    return [{ Location: 'Select All', selectAll: true }, ...filtered];
  };

  const handleLocationChange = (event, value) => {
    let nextSelectedLocations = [];

    if (value.some((location) => location.selectAll)) {
      nextSelectedLocations = locationSelectAll ? [] : locations;
    } else {
      nextSelectedLocations = value;
    }

    setSelectedLocations(nextSelectedLocations);
    setLocationSelectAll(
      locations.length > 0 && nextSelectedLocations.length === locations.length
    );

    // Keep location-dependent selection empty when all locations are unchecked.
    if (locations.length > 0 && nextSelectedLocations.length === 0) {
      setSelectedLocations([]);
      setErrors(prev => ({ ...prev, locations: true }));
      return;
    }

    if (errors.locations && nextSelectedLocations.length > 0) {
      setErrors(prev => ({ ...prev, locations: false }));
    }
  };

  const validateEmailFormat = (emailString) => {
    if (!emailString.trim()) return true; 
    
    const emails = emailString.split(',').map(e => e.trim()).filter(e => e);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    return emails.every(email => emailRegex.test(email));
  };

  const validateForm = () => {
    const newErrors = {
      frequency: !frequency,
      clients: selectedClients.length === 0,
      locations: locations.length > 0 && selectedLocations.length === 0,
      emails: !email.trim(),
      address: !address.trim(),
      time: !time 
    };

    if (email.trim() && !validateEmailFormat(email)) {
      newErrors.emails = true;
    }

  setErrors(newErrors);
  return !Object.values(newErrors).some(error => error);
};

const validateEditForm = () => {
    const newErrors = {
      clients: false,
      locations: false,
      emails: !editEmail.trim(),
      address: !editAddress.trim(),
      time: !editTime 
    };

    if (editEmail.trim() && !validateEmailFormat(editEmail)) {
      newErrors.emails = true;
    }

  setEditErrors(newErrors);
  return !Object.values(newErrors).some(error => error);
};

  const getFormattedEmails = () => {
    if (!email.trim()) return '';
    return email.split(',')
      .map(e => e.trim())
      .filter(e => e)
      .join(',');
  };

  const getClientIds = () => {
    return selectedClients.map(client => client.ID || client.id || 0);
  };

  const getClientsWithoutLocations = () => {
    
    return selectedClients
      .map((client) => Number(client.ID || client.id || 0))
      .filter((clientId) => clientId > 0 && !clientLocationAvailability[clientId]);
  };

  const getPayloadTargetIds = () => {
    
    if (locations.length > 0) {
      const selectedLocationIds = selectedLocations
        .map((location) => location.ID || location.id || 0)
        .filter((id) => Number(id) > 0);

      return [...selectedLocationIds, ...getClientsWithoutLocations()];
    }

    return getClientIds();
  };

  const createPayload = () => {
    return {
      Frequency: frequency,
      ClientId: getPayloadTargetIds(),
      BillingAddress: address.trim(),
      InvoiceEmails: getFormattedEmails(),
      Time: time,
      ClientInvoiceContactId : 0
    };
  };

  const normalizeClientIdentifier = (value) => {
    const text = String(value ?? '').trim();
    if (!text) return '';

    const numericValue = Number(text);
    return Number.isFinite(numericValue) ? String(numericValue) : text.toLowerCase();
  };

  const getInvoiceGroupClientIds = (group) => {
    const groupClients = Array.isArray(group?.Clients) ? group.Clients : [];

    return groupClients
      .map((client) => normalizeClientIdentifier(client?.client_id ?? client?.ClientId ?? client?.ID ?? client?.id))
      .filter(Boolean);
  };

  const hasDuplicateClientInformation = (payloadClientIds) => {
    const existingClientIds = new Set(
      (groupedInvoices || []).flatMap((group) => getInvoiceGroupClientIds(group))
    );

    return payloadClientIds
      .map((clientId) => normalizeClientIdentifier(clientId))
      .some((clientId) => clientId && existingClientIds.has(clientId));
  };

   const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      const payload = createPayload();

      if (hasDuplicateClientInformation(payload.ClientId)) {
        setNotification({
          open: true,
          message: DUPLICATE_CLIENT_MESSAGE,
          severity: 'error'
        });
        return;
      }

      setLoading(true);
      console.log('Submitting payload:', payload);
      
      addInvoiceGrouping(payload,(response)=>{
        if(response.status === 200 || response.status === 201){
          console.log('invoices added successfully')
          fetchInvoiceGrouping();
        }
      })

      setNotification({
        open: true,
        message: 'Invoice grouping saved successfully!',
        severity: 'success'
      });
      
      resetForm();
      
    } catch (error) {
      console.error('Error saving invoice grouping:', error);
      setNotification({
        open: true,
        message: 'Failed to save invoice grouping. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedClients([]);
    setSelectAll(false);
    setLocations([]);
    setSelectedLocations([]);
    setLocationSelectAll(false);
    setEmail('');
    setAddress('');
    setTime('00:00');
    setFrequency(1);
    setErrors({
      frequency: false,
      clients: false,
      locations: false,
      emails: false,
      address: false,
      time: false
    });
  };

  const handleFilterOptions = (options, params) => {
    const { inputValue } = params;
    const filtered = options.filter(option => option.displayname.toLowerCase().includes(inputValue.toLowerCase()));
    return [{ displayname: 'Select All', selectAll: true }, ...filtered];
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  
   const handleAddressChange = (e, isEdit = false) => {
    const value = e.target.value;
    if (isEdit) {
      setEditAddress(value);
      if (editErrors.address && value.trim()) {
        setEditErrors(prev => ({ ...prev, address: false }));
      }
    } else {
      setAddress(value);
      if (formErrors.address && value.trim()) {
        setFormErrors(prev => ({ ...prev, address: false }));
      }
    }
  };

  const handleTimeChange = (event, isEdit = false) => {
    if (isEdit) {
      setEditTime(event.target.value);
    } else {
      setTime(event.target.value);
    }
  };

    const handleBoxClick = (val, isEdit = false) => {
    if (val === 'email') {
      if (isEdit) {
        if (editEmail) {
          setEditEmail(editEmail + ',');
        } else {
          // Handle empty box logic for edit form
        }
      } else {
        if (email) {
          setEmail(email + ',');
        } else {
          // Handle empty box logic for add form
        }
      }
    }
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setEditEmail(group.InvoiceEmails);
    setEditTime(group.Time || '00:00');
    setEditFrequency(group.Frequency ?? group.frequency ?? group.Clients?.[0]?.frequency ?? 1);
    setEditAddress(group.BillingAddress);
    
    // Reset errors
    setEditErrors({
      clients: false,
      locations: false,
      emails: false,
      address: false,
      time: false
    });
    
    setEditDialogOpen(true);
  };

  const getEditPayloadTargetIds = () => {
    const editGroupClients = Array.isArray(editingGroup?.Clients) ? editingGroup.Clients : [];

    return editGroupClients
      .map((client) => Number(client?.client_id || 0))
      .filter((id) => id > 0);
  };

  const handleEditSubmit = async () => {
    if (!validateEditForm()) {
      return;
    }

    console.log(editingGroup)
    const ClientInvoiceContactId = editingGroup.InvoiceContactId ? editingGroup.InvoiceContactId : 0
    
    setEditLoading(true);
    
    try {
	     const clientId = getEditPayloadTargetIds();
     const emails = editEmail.split(',').map(e => e.trim()).filter(e => e).join(',');
     const payload = {
      Frequency: editFrequency,
      ClientId: clientId,
      BillingAddress: editAddress.trim(),
      InvoiceEmails: emails,
      Time: editTime,
      ClientInvoiceContactId : ClientInvoiceContactId
    };
    
    console.log('Updating payload:', payload);
      
    //updateInvoiceGrouping(payload, (response) => {
      addInvoiceGrouping(payload, (response) => {
      if (response.status === 200 || response.status === 201) {
        setNotification({
          open: true,
          message: 'Invoice grouping updated successfully!',
          severity: 'success'
        });
        setEditDialogOpen(false);
        fetchInvoiceGrouping();
      }
    });
    } catch (error) {
      console.error('Error updating invoice grouping:', error);
      setNotification({
        open: true,
        message: 'Failed to update invoice grouping. Please try again.',
        severity: 'error'
      });
    } finally {
      setEditLoading(false);
    }
  };

   const handleNumberChange = (event, index, type, isEdit = false) => {
    event.stopPropagation();
    if (type === 'email') {
      if (isEdit) {
        const numbersArray = editEmail.split(',');
        numbersArray[index] = event.target.value;
        const updatedNumbers = numbersArray.join(',');
        setEditEmail(updatedNumbers);
      } else {
        const numbersArray = email.split(',');
        numbersArray[index] = event.target.value;
        const updatedNumbers = numbersArray.join(',');
        setEmail(updatedNumbers);
      }
    }
  };


  const removeNumber = (e, index, type, isEdit = false) => {
    e.stopPropagation();
    if (type === 'email') {
      if (isEdit) {
        if (editEmail) {
          const numbersArray = editEmail.split(',');
          numbersArray.splice(index, 1);
          const updatedNumbers = numbersArray.join(',');
          if (updatedNumbers) {
            setEditEmail(updatedNumbers);
          } else {
            setEditEmail('');
          }
        }
      } else {
        if (email) {
          const numbersArray = email.split(',');
          numbersArray.splice(index, 1);
          const updatedNumbers = numbersArray.join(',');
          if (updatedNumbers) {
            setEmail(updatedNumbers);
          } else {
            setEmail('');
            setLastEmail(false);
          }
        }
      }
    }
  };

  const handleEditClose = () => {
		    setEditDialogOpen(false);
		    setEditingGroup(null);
		  };

  const getGroupedClientNames = (clients = []) => {
    const uniqueClientNames = [];
    const seenNames = new Set();

    clients.forEach((client) => {
      const clientName = String(client?.client_name || '').trim();
      const normalizedClientName = normalizeDisplayName(clientName);

      if (!normalizedClientName || seenNames.has(normalizedClientName)) {
        return;
      }

      seenNames.add(normalizedClientName);
      uniqueClientNames.push(clientName);
    });

    return uniqueClientNames;
  };

  const getGroupedLocations = (clients = []) =>
    clients
      .map((client) => String(client?.location ?? client?.Location ?? '').trim())
      .filter(Boolean);

	  const getCommaSeparatedClientNames = (clients = []) =>
	    getGroupedClientNames(clients).join(', ');

	  const getCommaSeparatedLocations = (clients = []) =>
	    getGroupedLocations(clients).join(', ');

	  const editLocationText = getCommaSeparatedLocations(editingGroup?.Clients || []);
	  
	  return (
    <Page title="Invoice Grouping">
      <Card variant="outlined" sx={{ p: 1 }}>
        <CardContent>
          <Typography variant="h4">
            Invoice Grouping & Time
          </Typography>

          {notification.open && (
            <Alert
              severity={notification.severity}
              onClose={() => setNotification((prev) => ({ ...prev, open: false }))}
              sx={{ mt: 2 }}
            >
              {notification.message}
            </Alert>
          )}

          <Grid container spacing={3} mt={1}>
            <Grid item xs={2}>
              <FormControl fullWidth>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={frequency}
                  onChange={(e) => {
                    setFrequency(e.target.value);
                    if (errors.frequency && e.target.value) {
                      setErrors(prev => ({ ...prev, frequency: false }));
                    }
                  }}
                  fullWidth
                  label="Frequency"
                  error={errors.frequency}
                >
                  <MenuItem value={1}>1</MenuItem>
                </Select>
                {errors.frequency && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      ml: 1.5
                    }}
                  >
                    Frequency is required
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={2}>
              <Autocomplete
                multiple
                fullWidth
                id="clients-autocomplete"
                options={uniqueAvailableClients}
                value={selectedClients}
                getOptionLabel={(option) =>
                  option.selectAll && selectedClients.length === uniqueAvailableClients.length
                    ? 'Select All'
                    : `${option.displayname || ''}`
                }
                onChange={handleClientChange}
                disableCloseOnSelect
                open={openAuto}
                onOpen={() => setOpenAuto(true)}
                onClose={() => setOpenAuto(false)}
                filterOptions={handleFilterOptions}
                renderTags={() => null}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.ID}>
                    {option.selectAll ? (
                      <>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selectAll}
                          disabled={uniqueAvailableClients.length === 0}
                        />
                        {option.displayname}
                      </>
                    ) : (
                      <>
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8 }}
                          checked={selectedClients.some(client => client.ID === option.ID)}
                        />
                        {option.displayname}
                      </>
                    )}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Client"
                    error={errors.clients}
                    helperText={errors.clients ? "Please select at least one client" : ""}
                    onFocus={() => setOpenAuto(true)}
                  />
                )}
              />
            </Grid>

            {selectedClients.length > 0 && locations.length > 0 && (
              <Grid item xs={2}>
                <Autocomplete
                  multiple
                  fullWidth
                  id="locations-autocomplete"
                  options={locations}
                  value={selectedLocations}
                  onChange={handleLocationChange}
                  disableCloseOnSelect
                  open={openLocationAuto}
                  onOpen={() => setOpenLocationAuto(true)}
                  onClose={() => setOpenLocationAuto(false)}
                  filterOptions={handleLocationFilterOptions}
                  getOptionLabel={(option) =>
                    option.selectAll ? 'Select All' : `${option.Location || ''}`
                  }
                  renderTags={() => null}
                  renderOption={(props, option) => (
                    <li {...props} key={option.ID || option.id || 'select-all'}>
                      {option.selectAll ? (
                        <>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={locationSelectAll}
                            disabled={locations.length === 0}
                          />
                          Select All
                        </>
                      ) : (
                        <>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selectedLocations.some(
                              (selectedLocation) => Number(selectedLocation.ID || selectedLocation.id) === Number(option.ID || option.id)
                            )}
                          />
                          {option.Location}
                        </>
                      )}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Locations"
                      placeholder="Search Locations..."
                      error={errors.locations}
                      helperText={errors.locations ? 'Please select at least one location' : ''}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={2}>
              <Box
                sx={{
                  border: (theme) => '1px solid ' + (errors.emails ? theme.palette.error.main : theme.palette.divider),
                  padding: '8px',
                  borderRadius: '8px',
                  minHeight: '56px',
                  cursor: 'text',
                  '&:hover': {
                    border: (theme) => '1px solid ' + (editErrors.emails ? theme.palette.error.main : theme.palette.text.primary),
                  }
                }}
                onClick={() => handleBoxClick('email', false)}
              >
                {!lastEmail && email.split(',').map((email, index) => (
                  <Box
                    key={index}
                    sx={{
                      marginBottom: 1,
                      width: '90%',
                      backgroundColor: (theme) => theme.palette.action.selected,
                      borderRadius: '100px',
                      position: 'relative',
                      border: 'none'
                    }}
                  >
                    <TextField
                      placeholder='Add Email'
                      value={email}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => handleNumberChange(e, index, 'email', false)}
                      fullWidth
                      size="small"
                      sx={{
                        '& input': {
                          height: '14px', 
                          fontSize: '14px',
                          color: (theme) => theme.palette.text.primary,
                          "&::placeholder": {
                            color: (theme) => theme.palette.text.secondary,
                            opacity: 1,
                          },
                        },
                        '& fieldset': {
                          border:'none'
                        },
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: '8px',
                        transform: 'translateY(-50%)',
                        width: '15px',
                        height: '15px',
                        borderRadius: '50%',
                        backgroundColor: (theme) => theme.palette.action.selected,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: '-8px',
                          right: '-8px',
                        }}
                        onClick={(e) => removeNumber(e, index, 'email', false)}
                      >
                        <CloseIcon fontSize="small" sx={{ fontSize: '14px', color: 'text.secondary' }} />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Box>
              {errors.emails && (
                <Typography 
                  variant="caption" 
                  color="error" 
                  sx={{ 
                    display: 'block',
                    mt: 0.5,
                    ml: 1
                  }}
                >
                  {email ? 'Enter valid email addresses' : 'Email is required'}
                </Typography>
              )}
            </Grid>
            
            <Grid item xs={1}>
              <FormControl fullWidth>
                <InputLabel>Time</InputLabel>
                <Select
                  value={time}
                  onChange={(e) => handleTimeChange(e, false)}
                  fullWidth
                  label="Time"
                  error={errors.time}
                >
                  {Array.from({ length: 24 * 2 }, (_, index) => {
                    const hours = Math.floor(index / 2).toString().padStart(2, '0');
                    const minutes = index % 2 === 0 ? '00' : '30';
                    const value = `${hours}:${minutes}`;
                    return (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.time && (
                  <Typography 
                    variant="caption" 
                    color="error" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      ml: 1.5
                    }}
                  >
                    Please select a time
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={2}>
              <Box sx={{ position: 'relative', width: '100%' }}>
                <Box
                  component="textarea"
                  minRows={2}
                  value={address}
                  onChange={(e) => handleAddressChange(e, false)}
                  placeholder="Enter Billing To"
                  sx={{
                    width: '100%',
                    minHeight: '56px',
                    padding: '16.5px 14px',
                    border: (theme) => '1px solid ' + (errors.address ? theme.palette.error.main : theme.palette.divider),
                    borderRadius: '4px',
                    fontFamily: 'inherit',
                    fontSize: '1rem',
                    resize: 'vertical',
                    lineHeight: '1.4375em',
                    backgroundColor: 'background.paper',
                    color: 'text.primary',
                    '&::placeholder': {
                      color: 'text.secondary',
                    },
                    '&:hover': {
                      borderColor: errors.address ? 'error.main' : 'text.primary',
                    },
                    '&:focus': {
                      outline: 'none',
                      borderColor: errors.address ? 'error.main' : 'primary.main',
                      borderWidth: '2px',
                    },
                  }}
                />
                {errors.address && (
                  <Typography 
                    variant="caption" 
                    color="error" 
                    sx={{ 
                      display: 'block',
                      mt: 0.5,
                      ml: 1.5
                    }}
                  >
                    Address is required
                  </Typography>
                )}
              </Box>
            </Grid>           

            <Grid item xs={1} mt={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit'}
              </Button>          
            </Grid>

            
          </Grid>

          <Grid container spacing={3} alignItems="center" sx={{ marginTop: "40px", width: '100%', marginLeft: '0px' }}>
           
              <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto' }}>
                <Table stickyHeader>
                  <TableHead sx={{ background: "#f3f3f3" }}>
                    <TableRow sx={{ background: '#f3f3f3' }}>
                      <TableCell>S.No</TableCell>
                      <TableCell>Frequency</TableCell>
                      <TableCell>Client Name</TableCell>
                      <TableCell>Locations</TableCell>
                      <TableCell>Emails</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell>Bill To</TableCell>
                      <TableCell>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {groupedInvoices.length === 0 ? (
                      <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                          <Typography color="textSecondary">
                            No invoice groupings found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      groupedInvoices.map((group, index) => {
                        const groupClients = Array.isArray(group?.Clients) ? group.Clients : [];
                        const clientNames = getGroupedClientNames(groupClients);
                        const locationNames = getGroupedLocations(groupClients);

                        return (
                        <TableRow key={group.InvoiceContactId || index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            {group.Frequency ?? group.frequency ?? group.Clients?.[0]?.frequency ?? 1}
                          </TableCell>
                          <TableCell>
                            {clientNames.length > 0 ? (
                              clientNames.map((clientName, clientIndex) => (
                                <Typography
                                  key={`${group.InvoiceContactId || index}-client-${clientIndex}`}
                                  variant="body2"
                                  sx={{ mb: clientIndex === clientNames.length - 1 ? 0 : 0.5 }}
                                >
                                  {clientName}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body2">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {locationNames.length > 0 ? (
                              locationNames.map((locationName, locationIndex) => (
                                <Typography
                                  key={`${group.InvoiceContactId || index}-location-${locationIndex}`}
                                  variant="body2"
                                  sx={{ mb: locationIndex === locationNames.length - 1 ? 0 : 0.5 }}
                                >
                                  {locationName}
                                </Typography>
                              ))
                            ) : (
                              <Typography variant="body2"></Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {String(group.InvoiceEmails || '')
                              .split(',')
                              .map((email, emailIndex) => (
                                <div key={emailIndex}>{email}</div>
                              ))}
                          </TableCell>
                          <TableCell>
                            {group.Time || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              style={{ 
                                whiteSpace: 'pre-wrap',
                                maxWidth: '300px',
                                wordBreak: 'break-word'
                              }}
                            >
                              {group.BillingAddress}
                            </Typography>
                          </TableCell>
                          <TableCell>
                          <Grid container spacing={2}>
                            <Grid item>
                              <Button
                                style={{
                                  color: "#fff"
                                }}
                                variant="contained" 
                                onClick={() => handleEdit(group)}
                              >
                                Edit
                              </Button>
                            </Grid>                                              
                          </Grid>
                        </TableCell>
                        </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
           
          </Grid>

	          <Dialog
	            open={editDialogOpen}
	            onClose={handleEditClose}
	            maxWidth="lg"
	            fullWidth
	          >
		            <DialogTitle>Edit Invoice Grouping</DialogTitle>
		            <DialogContent>
			              <Grid container spacing={2} mt={0.5}>
			                <Grid item xs={12}>
			                  <Typography variant="body1" sx={{ mb: 0.5 }}>
			                    <Box component="span" sx={{ fontWeight: 700 }}>
			                      Frequency:
			                    </Box>{' '}
			                    {editFrequency || '-'}
			                  </Typography>
			                </Grid>

			                <Grid item xs={12}>
			                  <Typography variant="body1" sx={{ mb: 0.5 }}>
			                    <Box component="span" sx={{ fontWeight: 700 }}>
			                      Client:
			                    </Box>{' '}
			                    {getCommaSeparatedClientNames(editingGroup?.Clients || []) || '-'}
			                  </Typography>
			                </Grid>

			                {editLocationText && (
			                <Grid item xs={12}>
			                  <Typography variant="body1" sx={{ mb: 0.5 }}>
			                    <Box component="span" sx={{ fontWeight: 700 }}>
			                      Locations:
			                    </Box>{' '}
			                    {editLocationText}
			                  </Typography>
			                </Grid>
			                )}

		                <Grid item xs={3}>
		                  <Box
                    sx={{
                      border: '1px solid',
                      border: (theme) => '1px solid ' + (editErrors.emails ? theme.palette.error.main : theme.palette.divider),
                      padding: '8px',
                      borderRadius: '8px',
                      minHeight: '56px',
                      cursor: 'text',
                      '&:hover': {
                        border: (theme) => '1px solid ' + (editErrors.emails ? theme.palette.error.main : theme.palette.text.primary),
                      }
                    }}
                    onClick={() => handleBoxClick('email', true)}
                  >
                    {editEmail.split(',').map((email, index) => (
                      <Box
                        key={index}
                        sx={{
                          marginBottom: 1,
                          width: '90%',
                          backgroundColor: (theme) => theme.palette.action.selected,
                          borderRadius: '100px',
                          position: 'relative',
                          border: 'none'
                        }}
                      >
                        <TextField
                          placeholder='Add Email'
                          value={email}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => handleNumberChange(e, index, 'email', true)}
                          fullWidth
                          size="small"
                          sx={{
                            '& input': {
                              height: '14px', 
                              fontSize: '14px',
                              color: (theme) => theme.palette.text.primary,
                              "&::placeholder": {
                                color: (theme) => theme.palette.text.secondary,
                                opacity: 1,
                              },
                            },
                            '& fieldset': {
                              border:'none'
                            },
                          }}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            right: '8px',
                            transform: 'translateY(-50%)',
                            width: '15px',
                            height: '15px',
                            borderRadius: '50%',
                            backgroundColor: (theme) => theme.palette.action.selected,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}
                        >
                          <IconButton
                            sx={{
                              position: 'absolute',
                              top: '-8px',
                              right: '-8px',
                            }}
                            onClick={(e) => removeNumber(e, index, 'email', true)}
                          >
                            <CloseIcon fontSize="small" sx={{ fontSize: '14px', color: 'text.secondary' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  {editErrors.emails && (
                    <Typography 
                      variant="caption" 
                      color="error" 
                      sx={{ 
                        display: 'block',
                        mt: 0.5,
                        ml: 1
                      }}
                    >
                      {editEmail ? 'Enter valid email addresses' : 'Email is required'}
                    </Typography>
                  )}
                </Grid>
                
                <Grid item xs={2}>
                  <FormControl fullWidth>
                    <InputLabel>Time</InputLabel>
                    <Select
                      value={editTime}
                      onChange={(e) => handleTimeChange(e, true)}
                      fullWidth
                      label="Time"
                      error={editErrors.time}
                    >
                      {Array.from({ length: 24 * 2 }, (_, index) => {
                        const hours = Math.floor(index / 2).toString().padStart(2, '0');
                        const minutes = index % 2 === 0 ? '00' : '30';
                        const value = `${hours}:${minutes}`;
                        return (
                          <MenuItem key={value} value={value}>
                            {value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {editErrors.time && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ 
                          display: 'block',
                          mt: 0.5,
                          ml: 1.5
                        }}
                      >
                        Please select a time
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={3}>
                  <Box sx={{ position: 'relative', width: '100%' }}>
                    <Box
                      component="textarea"
                      minRows={3}
                      value={editAddress}
                      onChange={(e) => handleAddressChange(e, true)}
                      placeholder="Enter Billing To"
                      sx={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '16.5px 14px',
                        border: (theme) => '1px solid ' + (editErrors.address ? theme.palette.error.main : theme.palette.divider),
                        borderRadius: '4px',
                        fontFamily: 'inherit',
                        fontSize: '1rem',
                        resize: 'vertical',
                        lineHeight: '1.4375em',
                        backgroundColor: 'background.paper',
                        color: 'text.primary',
                        '&::placeholder': {
                          color: 'text.secondary',
                        },
                        '&:hover': {
                          borderColor: editErrors.address ? 'error.main' : 'text.primary',
                        },
                        '&:focus': {
                          outline: 'none',
                          borderColor: editErrors.address ? 'error.main' : 'primary.main',
                          borderWidth: '2px',
                        },
                      }}
                    />
                    {editErrors.address && (
                      <Typography 
                        variant="caption" 
                        color="error" 
                        sx={{ 
                          display: 'block',
                          mt: 0.5,
                          ml: 1.5
                        }}
                      >
                        Address is required
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose} color="error" variant='outlined'>
                Cancel
              </Button>
              <Button 
                onClick={handleEditSubmit} 
                variant="contained" 
                color="primary"
                disabled={editLoading}
              >
                {editLoading ? 'Updating...' : 'Update'}
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Page>
  );
};

export default InvoiceGrouping;
